import type { Extension } from '@atlassian/forge-ui/web-client/graphql/types';

import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { containsNetworkError } from '@confluence/network';

import type { ForgeModules } from '../ForgeModules.ts';

import { type UseExtensionListProps } from './useExtensionList.tsx';
import { preloadForgeApps } from './preloadForgeApps.ts';

const setWindowExtensions = (
	moduleType: string,
	extensions: Extension[],
	error: Error | undefined,
	context: {
		spaceKey: string;
		contentId?: string;
		includeHidden: boolean;
		spaceContextOnly: boolean;
	},
) => {
	window.__SSR_FORGE_EXTENSIONS__ = window.__SSR_FORGE_EXTENSIONS__ || {};
	window.__SSR_FORGE_EXTENSIONS__[moduleType] = {
		extensions,
		error,
		context,
	};
};

export async function preloadUseExtensionList({
	contentId,
	spaceKey,
	moduleType,
	additionalModuleTypes = [],
	includeHidden = false,
	spaceContextOnly = false,
}: UseExtensionListProps & {
	contentId?: string;
	spaceKey: string;
	moduleType: ForgeModules;
	additionalModuleTypes?: ForgeModules[];
	includeHidden?: boolean;
}): Promise<void> {
	const { cloudId, locale } = getPreloaderFnContext();
	const moduleTypes = [moduleType, ...additionalModuleTypes];

	return SSRMeasures.run('ssr-app/prepare/preloadQuery/fetch:ForgeExtensionList', async () => {
		try {
			const result = await preloadForgeApps({
				cloudId,
				locale,
				types: moduleTypes,
				context: spaceContextOnly
					? { spaceKey }
					: {
							spaceKey,
							contentId: contentId != null ? Number(contentId) : undefined,
						},
				includeHidden,
			});

			if (result.hasErrors) {
				// Leave discovery unknown while reporting only unexpected, unhandled errors.
				await waitForAllReturnWhenError(result);
				return;
			}

			const { extensions } = result;
			for (const type of moduleTypes) {
				setWindowExtensions(
					type,
					additionalModuleTypes.length > 0
						? (extensions ?? []).filter((extension) => extension.type === type)
						: (extensions ?? []),
					undefined,
					{ spaceKey, contentId, includeHidden, spaceContextOnly },
				);
			}
		} catch (e) {
			// Want to handle network errors to not affect experience SLA
			if (containsNetworkError(e)) {
				markErrorAsHandled(e);
			} else {
				ssrPrepareErrorsClient.submitError(
					new Error(`Filed to get forge extension list: ${e.message}`),
				);
			}
		}
	});
}
